// K6 kiosk fleet — API wrappers.
// Contract: KIOSK repo docs/K6-CONTRACT.md. All times are epoch milliseconds;
// the server never recomputes money — rows are verbatim (the ONE derived figure,
// the court's side on the public summary, is labelled as such by the server).
//
// Two audiences:
//   - fleet management → SUPER ADMIN: /api/sports/admin/kiosk with the RETAIL
//     bearer (agripos_token, is_super_admin), the way sports_admin.py expects.
//   - the court's public summary → /api/sports/kiosk/court, no login; the
//     staff PIN unlocks a short-lived session token sent as X-Court-Session.
import axios from 'axios';
import { sportsApi } from '../SportsAuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN = `${BACKEND}/api/sports/admin/kiosk`;
const COURT = `${BACKEND}/api/sports/kiosk/court`;

function adminApi() {
  const token = localStorage.getItem('agripos_token');
  return axios.create({ baseURL: ADMIN, headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

// ── Super admin ───────────────────────────────────────────────────────────────
export const listDevices = () => adminApi().get('/devices').then(r => r.data);
export const getDevice = (id) => adminApi().get(`/devices/${id}`).then(r => r.data);
// 7.6: "Check now" — a content-free nudge → { sent, reason, attempts?, last_seen_ms, pinged_at_ms, message }; 409 device_inactive, 429 too_soon (10 s)
export const pingDevice = (id) => adminApi().post(`/devices/${id}/ping`).then(r => r.data);
// { name?, site_name?, notes?, court_org_id?: string|null }  — null unlinks the tenant
export const updateDevice = (id, body) => adminApi().put(`/devices/${id}`, body).then(r => r.data);
export const revokeDevice = (id, body = {}) => adminApi().post(`/devices/${id}/revoke`, body).then(r => r.data);   // body.owner_code: the owner's key (7.0e(g))
// { code, name, site_name, court_org_id?, replace_kiosk_id? } → { kiosk_id }
export const pairDevice = (body) => adminApi().post('/devices/pair', body).then(r => r.data);
export const rotateCourtLink = (id, body = {}) => adminApi().post(`/devices/${id}/court-link/rotate`, body).then(r => r.data);
export const getTotpSetup = () => adminApi().get('/totp/setup').then(r => r.data);
// K8 P2: who is emailed on emergency / machine problem / dead phone / late stop → { emails, kinds, kinds_available, kind_labels, email_configured }
export const getKioskAlerts = () => adminApi().get('/alerts').then(r => r.data);
export const setKioskAlerts = (body) => adminApi().put('/alerts', body).then(r => r.data);
export const rotateTotp = () => adminApi().post('/totp/rotate').then(r => r.data);
// 7.0e: the fleet owner PIN — one for every phone, set here or in the app, delivered on the beat.
export const getOwnerPin = () => adminApi().get('/owner-pin').then(r => r.data);
export const setOwnerPin = (body) => adminApi().put('/owner-pin', body).then(r => r.data);
// params: { kiosk_id?, table?, from_ms?, to_ms?, court_org_id?, limit? } → { rows, count }
export const queryRows = (params) => adminApi().get('/rows', { params }).then(r => r.data);
// [{ org_id, name, slug, account_status, devices_enabled }]
export const listTenants = () => adminApi().get('/tenants').then(r => r.data);
// K8 P2.5 P4: the fleet in one call (the Management App's dashboard) → { fleet, live: [...], today, attention, recent_alerts, devices }
export const getSummary = () => adminApi().get('/summary').then(r => r.data);

// ── Phase 2: sales / review / scorecard / web collection (super admin) ────────
// Every money figure these return is DERIVED on the server from the phone's
// rows with the phone's own split rule; the responses say so (`derived`).
// params: { from: 'YYYY-MM-DD', to, group: 'device'|'site'|'tenant', period?: 'since_paid'|'all' } → { period, group, groups, totals }
// K8: `period` selects every unsettled rental / everything and ignores from/to.
export const getSales = (params) => adminApi().get('/sales', { params }).then(r => r.data);
// params: { from, to, kiosk_id?, needs_attention?: 1 } → { period, items, counts }
export const getReview = (params) => adminApi().get('/review', { params }).then(r => r.data);
// params: { from, to } → { period, devices: [scorecard...], fleet_stop_pct_avg }
export const getScorecard = (params) => adminApi().get('/scorecard', { params }).then(r => r.data);
// 2026-09-30: the billing statement — params {period: 'since_paid'} or {period: 'range', from, to}
export const getBillingStatement = (id, params) => adminApi().get(`/devices/${id}/billing`, { params }).then(r => r.data);
// → { plan: null | {...}, reason }
export const getCollectPlan = (id) => adminApi().get(`/devices/${id}/collect/plan`).then(r => r.data);
// { expected_gross_centavos, expected_from_epoch_day, expected_to_epoch_day, note? } → { collection, plan }; 409 when the plan moved
export const commitCollect = (id, body) => adminApi().post(`/devices/${id}/collect`, body).then(r => r.data);

// ── 7.0f: collection statements (super admin) ────────────────────────────────
// One statement per settlement of EITHER kind (web or phone); the phone's echo of a
// web settlement is folded onto the web entry. A ref is a web collection id or "phone:<row_id>".
// → { kiosk_id, device, statements[], count, settled_through, derived_note, server_now_ms }
export const getDeviceStatements = (id) => adminApi().get(`/devices/${id}/statements`).then(r => r.data);
// → the statement, owner audience (every figure): { id, source, device, period, collected_at_ms, by, authorised_by,
//   acked_by_phone, note, totals, reconciled, rentals[], issues[], prior, derived_note }
export const getDeviceStatement = (id, ref) => adminApi().get(`/devices/${id}/statements/${encodeURIComponent(ref)}`).then(r => r.data);

// ── Phase 3: commands (super admin) ──────────────────────────────────────────
// { code, args?, client_key?, confirmed_call?, fleet_confirm? } → { command_id, status, superseded, fleet_count, replayed }
// 400 {code:'call_first'} for FE without confirmed_call; 422 on bad args;
// 429 {code:'fleet_confirm', devices} when RATE/SHARE/SITE/PIN went to > 5 devices in 10 min (resend with fleet_confirm: true).
export const issueCommand = (id, body) => adminApi().post(`/devices/${id}/commands`, body).then(r => r.data);
export const listDeviceCommands = (id) => adminApi().get(`/devices/${id}/commands`).then(r => r.data);
// params: { status?: 'pending'|'delivered'|'done'|'failed'|'expired'|'open', kiosk_id?, from?, to?, limit? } → { commands, count }
export const listCommands = (params) => adminApi().get('/commands', { params }).then(r => r.data);
// The DIAG text (text/plain) — fetched with the bearer, so it cannot be a plain link.
export const getDiagnostics = (commandId) => adminApi().get(`/commands/${commandId}/diagnostics`, { responseType: 'text' }).then(r => r.data);
export const newClientKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// ── Phase 4: ball machine vouchers (super admin) ─────────────────────────────
// { prefix, count, type: 'flat'|'percent'|'free_time', value, valid_from?, valid_until, label?,
//   court_pct, owner_pct (sum 100), reason, device_ids[], site_names[], min_booked_minutes } → { batch_id, count, codes }
export const generateKioskVouchers = (body) => adminApi().post('/vouchers/generate', body).then(r => r.data);
// params: { status?, batch_id?, kiosk_id? } → { vouchers, count, batches }
export const listKioskVouchers = (params) => adminApi().get('/vouchers', { params }).then(r => r.data);
export const kioskVoucherStats = () => adminApi().get('/vouchers/stats').then(r => r.data);
export const deleteKioskVoucher = (id) => adminApi().delete(`/vouchers/${id}`).then(r => r.data);
export const deleteKioskVoucherBatch = (batchId) => adminApi().delete(`/vouchers/batch/${batchId}`).then(r => r.data);
// Voucher manager (2026-10-01): a new end date for the batch's UNUSED codes → { updated, valid_until }.
export const extendKioskVoucherBatch = (batchId, validUntil) => adminApi().put(`/vouchers/batches/${batchId}/extend`, { valid_until: validUntil }).then(r => r.data);

// ── Phase 4: the tenant's court-funded ball machine vouchers (sports JWT) ────
// Same generator as the court's own vouchers with channel 'kiosk'; the server forces
// court_pct = 100 and restricts device_ids to the phones linked to the org (403 otherwise).
export const mintTenantKioskVouchers = (body) => sportsApi.post('/sports/vouchers/generate', { ...body, channel: 'kiosk' }).then(r => r.data);
export const listTenantKioskVouchers = () => sportsApi.get('/sports/vouchers', { params: { channel: 'kiosk' } }).then(r => r.data);
export const tenantKioskVoucherStats = () => sportsApi.get('/sports/vouchers/stats', { params: { channel: 'kiosk' } }).then(r => r.data);
// Voucher manager (2026-10-01): the court's own codes + HighMark's codes for its machine(s), read-only (`from_highmark`).
export const listTenantKioskVoucherManager = () => sportsApi.get('/sports/vouchers', { params: { channel: 'kiosk', include_highmark: true } }).then(r => r.data);
export const extendTenantKioskBatch = (batchId, validUntil) => sportsApi.put(`/sports/vouchers/batches/${batchId}/extend`, { valid_until: validUntil }).then(r => r.data);
export const deleteTenantKioskBatch = (batchId) => sportsApi.delete(`/sports/vouchers/batch/${batchId}`).then(r => r.data);
export const deleteTenantKioskVoucher = (id) => sportsApi.delete(`/sports/vouchers/${id}`).then(r => r.data);

// ── Phase 2: tenant view (sports JWT via sportsApi's interceptor) ─────────────
// params: { period: 'since_paid'|'day'|'last7'|'week'|'month'|'all', anchor: 'YYYY-MM-DD' } — K8: since_paid is the default,
// rolling kinds (since_paid, last7, all) end today and ignore the anchor; the reply carries `live` and `settlement`.
export const mineSummary = (params) => sportsApi.get('/sports/kiosk/mine/summary', { params }).then(r => r.data);
// 7.0f: the court's copy of one settlement's statement (court audience — no owner figure). `kioskId` says
// which of the tenant's phones when a phone row id could belong to two of them (409 ambiguous_ref otherwise).
export const mineStatement = (ref, kioskId) =>
  sportsApi.get(`/sports/kiosk/mine/statements/${encodeURIComponent(ref)}`, { params: kioskId ? { kiosk_id: kioskId } : {} }).then(r => r.data);

// Local calendar date as YYYY-MM-DD (the browser's day; the server reads it as a Manila day).
export const ymd = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const shiftYmd = (s, days) => {
  const [y, m, d] = s.split('-').map(Number);
  return ymd(new Date(y, m - 1, d + days));
};
// Epoch day → YYYY-MM-DD (noon UTC keeps every timezone on the same calendar day).
export const epochDayToYmd = (n) => (n == null ? '' : new Date(Number(n) * 86400000 + 12 * 3600000).toISOString().slice(0, 10));

// 401/403 from the admin routes = not signed in as super admin (or the retail token aged out).
export const isAdminDenied = (err) => [401, 403].includes(err?.response?.status);

// The court's link as the fleet page shows it: the server's url when it has a
// frontend base configured, otherwise this origin (same app, /m/{code}).
export const courtLinkUrl = (link) => {
  if (!link?.code) return '';
  return link.url || `${window.location.origin}/m/${link.code}`;
};

// ── Court public summary (no login) ───────────────────────────────────────────
export const courtInfo = (code) => axios.get(`${COURT}/${encodeURIComponent(code)}`).then(r => r.data);
export const courtUnlock = (code, pin) => axios.post(`${COURT}/${encodeURIComponent(code)}/unlock`, { pin }).then(r => r.data);
export const courtSummary = (code, sessionToken, date) =>
  axios.get(`${COURT}/${encodeURIComponent(code)}/summary`, {
    params: date ? { date } : {},
    headers: { 'X-Court-Session': sessionToken },
  }).then(r => r.data);
// 7.0f: every settlement of the phone behind the link, and one statement (court audience), same session header.
export const courtStatements = (code, sessionToken) =>
  axios.get(`${COURT}/${encodeURIComponent(code)}/statements`, { headers: { 'X-Court-Session': sessionToken } }).then(r => r.data);
export const courtStatement = (code, sessionToken, ref) =>
  axios.get(`${COURT}/${encodeURIComponent(code)}/statements/${encodeURIComponent(ref)}`, { headers: { 'X-Court-Session': sessionToken } }).then(r => r.data);

export const errDetail = (err) => err?.response?.data?.detail;

export const errMsg = (err, fallback = 'Something went wrong') => {
  const d = errDetail(err);
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};
